// Copyright © 2021 The Things Industries B.V.

import React, { useCallback, useState } from 'react'
import { defineMessages } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'

import AZURE_IOT_HUB_INTEGRATION from '@console/constants/azure-iot-hub-integration.tti'

import Form from '@ttn-lw/components/form'
import Input from '@ttn-lw/components/input'
import ModalButton from '@ttn-lw/components/button/modal-button'
import SubmitBar from '@ttn-lw/components/submit-bar'
import SubmitButton from '@ttn-lw/components/submit-button'
import toast from '@ttn-lw/components/toast'
import { IconTrash } from '@ttn-lw/components/icon'

import {
  AzureIoTMessages,
  AzureIoTApplicationUpFilterSchema,
  AzureIoTDefaultApplicationUpFilterValues,
  CreateAzureIoTApplicationUpFilterField,
} from '@console/containers/azure-iot-integration/shared/index.tti'

import Yup from '@ttn-lw/lib/yup'
import sharedMessages from '@ttn-lw/lib/shared-messages'
import { isNotFoundError } from '@ttn-lw/lib/errors/utils'
import attachPromise from '@ttn-lw/lib/store/actions/attach-promise'
import { hostname as hostnameRegexp } from '@ttn-lw/lib/regexp'

import {
  setAppPkgDefaultAssoc,
  deleteAppPkgDefaultAssoc,
} from '@console/store/actions/application-packages'

import { selectSelectedApplicationId } from '@console/store/selectors/applications'
import {
  selectApplicationPackageDefaultAssociation,
  selectGetApplicationPackagesError,
} from '@console/store/selectors/application-packages'

const m = defineMessages({
  accessKey: 'Azure IoT Hub access key',
  accessKeyDescription: 'Copy the Azure deployment "iotHubOwnerKey" output',
  delete: 'Delete Azure IoT Hub integration',
  deleted: 'Azure IoT Hub integration deleted',
  enable: 'Enable Azure IoT Hub integration',
  enabled: 'Azure IoT Hub integration enabled',
  hostName: 'Azure IoT Hub hostname',
  hostNameDescription: 'Copy the Azure deployment "iotHubHostname" output',
  update: 'Update Azure IoT Hub integration',
  updated: 'Azure IoT Hub integration updated',
  validateHostNameFormat: '{field} must be a valid Azure IoT Hostname',
})

const validationSchema = Yup.object()
  .shape({
    data: Yup.object().shape({
      host_name: Yup.string()
        .matches(hostnameRegexp, Yup.passValues(m.validateHostNameFormat))
        .required(sharedMessages.validateRequired),
      access_key: Yup.string().required(sharedMessages.validateRequired),
      reported_properties: AzureIoTApplicationUpFilterSchema,
      send_event: AzureIoTApplicationUpFilterSchema,
    }),
  })
  .noUnknown()

const defaultValues = {
  data: {
    host_name: '',
    access_key: '',
    reported_properties: AzureIoTDefaultApplicationUpFilterValues,
    send_event: AzureIoTDefaultApplicationUpFilterValues,
  },
}

const promisifiedSetAppPkgDefaultAssoc = attachPromise(setAppPkgDefaultAssoc)
const promisifiedDeleteAppPkgDefaultAssoc = attachPromise(deleteAppPkgDefaultAssoc)

const AzureIoTHubIntegrationForm = () => {
  const [error, setError] = useState(undefined)
  const appId = useSelector(selectSelectedApplicationId)

  const dispatch = useDispatch()
  const defaultAssociation = useSelector(state =>
    selectApplicationPackageDefaultAssociation(state, AZURE_IOT_HUB_INTEGRATION.DEFAULT_PORT),
  )
  const packageError = useSelector(selectGetApplicationPackagesError)
  const initialValues = validationSchema.cast(defaultAssociation || defaultValues)

  const handleSubmit = useCallback(
    async values => {
      try {
        await dispatch(
          promisifiedSetAppPkgDefaultAssoc(appId, AZURE_IOT_HUB_INTEGRATION.DEFAULT_PORT, {
            package_name: AZURE_IOT_HUB_INTEGRATION.DEFAULT_PACKAGE_NAME,
            ...values,
          }),
        )
        toast({
          message: Boolean(defaultAssociation) ? m.updated : m.enabled,
          type: toast.types.SUCCESS,
        })
      } catch (error) {
        setError(error)
      }
    },
    [appId, dispatch, defaultAssociation],
  )

  const handleDelete = useCallback(async () => {
    try {
      await dispatch(
        promisifiedDeleteAppPkgDefaultAssoc(appId, AZURE_IOT_HUB_INTEGRATION.DEFAULT_PORT, {
          package_name: AZURE_IOT_HUB_INTEGRATION.DEFAULT_PACKAGE_NAME,
        }),
      )
      toast({
        title: sharedMessages.azureIoTHub,
        message: m.deleted,
        type: toast.types.SUCCESS,
      })
    } catch (error) {
      setError(error)
    }
  }, [appId, dispatch])

  if (packageError && !isNotFoundError(packageError)) {
    throw packageError
  }

  return (
    <Form
      error={error}
      validationSchema={validationSchema}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      <Form.Field
        name="data.host_name"
        title={m.hostName}
        component={Input}
        description={m.hostNameDescription}
        required
      />
      <Form.Field
        name="data.access_key"
        title={m.accessKey}
        component={Input}
        description={m.accessKeyDescription}
        sensitive
        required
      />
      {CreateAzureIoTApplicationUpFilterField({
        name: 'data.reported_properties',
        title: AzureIoTMessages.reportedPropertiesFilter,
        info: AzureIoTMessages.reportedPropertiesFilterInfo,
      })}
      {CreateAzureIoTApplicationUpFilterField({
        name: 'data.send_event',
        title: AzureIoTMessages.eventFilter,
        info: AzureIoTMessages.eventFilterInfo,
      })}
      <SubmitBar>
        <Form.Submit
          component={SubmitButton}
          message={Boolean(defaultAssociation) ? m.update : m.enable}
        />
        {Boolean(defaultAssociation) && (
          <ModalButton
            type="button"
            message={m.delete}
            modalData={{
              message: sharedMessages.integrationDeleteWarning,
            }}
            onApprove={handleDelete}
            icon={IconTrash}
            danger
            naked
          />
        )}
      </SubmitBar>
    </Form>
  )
}

export default AzureIoTHubIntegrationForm
